/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState, useCallback, useEffect, useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { getUser } from "../../utils/helpers";
import { updatePathWithTabs } from "../../utils/Routes";
import {
  chemQueue, pendingQueue, markTestRunOnInstrument,
} from "../../actions/chemistry";
import { updateTestResult } from "../../actions/sendReports";
import ChemBioPending from "../Common/ChemBioPending/ChemBioPending";
import ChemUpload from "../Components/Chemistry/ChemUpload";
import Platemap from "../Components/Chemistry/Components/Platemap/Platemap";
import ChemPesticides from "../Components/Chemistry/Components/Pesticides/ChemPesticides";
import Sequence from "../Components/Chemistry/Components/Sequence/Sequence";

import "../Components/Chemistry/Chemistry.css";

const AntTabs = withStyles({
  root: {
    backgroundColor: "#F4F5F7",
    borderBottom: "1px solid #e8e8e8",
  },
  indicator: {
    backgroundColor: "#494B83",
  },
})(Tabs);

const AntTab = withStyles((theme) => ({
  root: {
    textTransform: "none",
    minWidth: 160,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontSize: "14px",
    lineHeight: "24px",
    color: "#6B778C",
    fontFamily: ["Roboto Bold"].join(","),
    "&:hover": {
      color: "#494B83",
      opacity: 1,
    },
    "&$selected": {
      color: "#494B83",
      fontWeight: theme.typography.fontWeightMedium,
    },
    "&:focus": {
      color: "#494B83",
    },
  },
  selected: {},
}))((props) => <Tab disableRipple {...props} />);

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  padding: {
    padding: theme.spacing(3),
  },
  chem: {
    backgroundColor: "#F4F5F7",
  },
}));

const pesticidesColumn = [
  {
    title: "Customer",
    dataIndex: "customer_id",
    key: "customer_id",
  },
  {
    title: "WO",
    dataIndex: "workorder_id",
    key: "workorder_id",
  },
  {
    title: "Genista ID",
    dataIndex: "genista_id",
    key: "genista_id",
  },
  {
    title: "Test",
    dataIndex: "test_name",
    key: "test_name",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
  },
  {
    title: <div className="ChemPesticidesTable__Action">Action</div>,
    dataIndex: "action",
    key: "action",
    width: "120px",
    className: "ChemPesticidesTable__Action",
  },
];

const pesticideSampleInfo = [
  {
    label: "Customer ID",
    key: "customer_id",
  },
  {
    label: "Work order",
    key: "workorder_id",
  },
  {
    label: "Genista ID",
    key: "genista_id",
  },
  {
    label: "Sample ID",
    key: "coc_sample_id",
  },
  {
    label: "Sample Type",
    key: "sample_type",
  },
  {
    label: "Test",
    key: "test_name",
  },
  {
    label: "Commodity",
    key: "commodity",
  },
  {
    label: "Instruments",
    key: "instrument_ids",
  },
];

function TabPanel(props) {
  const {
    children, value, index, ...other
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chem-tabpanel-${index}`}
      aria-labelledby={`chem-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

TabPanel.defaultProps = {
  children: null,
};

function a11yProps(index) {
  return {
    id: `chem-tab-${index}`,
    "aria-controls": `chem-tabpanel-${index}`,
  };
}

export default function ChemistryTab() {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const history = useHistory();
  const page = "chemistry";
  const index = undefined;
  const currentPath = undefined;
  const user = useMemo(() => getUser(), []);
  const tabs = useMemo(() => [
    {
      tabLabel: "Upload data", route: "uploaddata", permission: "view_automation_chemistry_upload", component: <ChemUpload />,
    },
    {
      tabLabel: "Pending",
      route: "pending",
      permission: "view_automation_chemistry_pending",
      component:
  <div className="customTabChem">
    <ChemBioPending
      getPendingResults={pendingQueue}
      searchPendingResult={chemQueue}
      helperFn={{ markTestRunOnInstrument, updateTestResult }}
      showSearch
      chemDropdown
    />
  </div>,
    },
    {
      tabLabel: "MRL",
      route: "mrl",
      permission: "view_automation_chemistry_mrl",
      component:
  <div className="customTabChem">
    <ChemPesticides
      tableColumns={pesticidesColumn}
      pesticideSampleInfo={pesticideSampleInfo}
      batchReportView={false}
    />
  </div>,
    },
    {
      tabLabel: "Platemap",
      route: "platemap",
      permission: "view_automation_chemistry_platemap",
      component:
  <div className="customTabChem ChemTabMinWidth">
    <Platemap />
  </div>,
    },
    {
      tabLabel: "Sequence",
      route: "sequence",
      permission: "view_sequence",
      component:
  <div className="customTabChem">
    <Sequence />
  </div>,
    },
  ], []);
  const authorizedTabs = useMemo(() => tabs.filter((item) => user.permissionMap[item.permission] || item.permission === "no-permission").map((item, i) => ({ ...item, tabValue: i })), [tabs, user]);
  const handleChange = useCallback((event, newValue) => {
    // Depending on the value of the tab, update the path
    updatePathWithTabs(currentPath, newValue, page, setValue, authorizedTabs, history);
    // Update the state
    setValue(newValue);
  }, [history, currentPath, authorizedTabs]);

  useEffect(() => {
    const path = window.location.pathname;
    // On refresh, makes sure the state is set correctly
    updatePathWithTabs(path, index, page, setValue, authorizedTabs, history);

    // Handles the case when the back button on the browser is clicked
    const unlisten = history.listen((location, action) => {
      if (action === "POP") {
        updatePathWithTabs(location.pathname, index, page, setValue, authorizedTabs, history);
      }
    });

    // Cleanup subscription on component unmount
    return () => {
      unlisten();
    };
  }, []); // eslint-disable-line

  return (
    <div className={classes.root}>
      <div className={classes.chem}>
        <AntTabs value={value} onChange={handleChange} aria-label="ant tabs">
          {authorizedTabs.map((item) => (<AntTab key={item.tabValue} label={item.tabLabel} {...a11yProps(item.tabValue)} />))}
        </AntTabs>
        {authorizedTabs.map((item) => (
          <TabPanel key={item.tabValue} value={value} index={item.tabValue}>
            {item.component}
          </TabPanel>
        ))}
      </div>
    </div>
  );
}
